import { EnrichmentCategory, EnrichmentCategoryKind, EnrichmentCategorySide } from 'vopay';

export type CategoryKind = EnrichmentCategoryKind;

export type CategoryDescriptor = {
  // EnrichedDataType slug of the category's report endpoint. Kept as string:
  // the backend can serve categories the static union doesn't know yet.
  reportType: string;
  // Backend `Classification` value on raw aggregator transaction rows
  classification: string;
  // Whether the category's rows are credits or debits
  side: EnrichmentCategorySide;
  labelKey: string;
  defaultLabel: string;
  kind: CategoryKind;
};

// Categories come from GET /iq11/enrich-data/categories; the lists below are
// the fallback when that endpoint is unavailable. Known classifications keep
// their translation keys; new ones fall back to the backend display name.
export function toCategoryDescriptor(category: EnrichmentCategory): CategoryDescriptor {
  const known = ALL_CATEGORIES.find((descriptor) => descriptor.classification === category.Classification);

  return {
    reportType: category.ReportType,
    classification: category.Classification,
    side: category.Side,
    kind: category.Kind,
    labelKey: known?.labelKey ?? category.ReportType,
    defaultLabel: known?.defaultLabel ?? category.DisplayName,
  };
}

export function categoriesOfKind(categories: CategoryDescriptor[], kind: CategoryKind): CategoryDescriptor[] {
  return categories.filter((category) => category.kind === kind);
}

// Slice colors come from the ordinal blue ramp (largest slice = darkest).
// The ramp passes the ordinal palette checks (monotone lightness, visible step
// gaps, light end clears the surface) — do not reorder or lighten it.
export const CATEGORY_RAMP = ['#0A2456', '#12418F', '#1C60CF', '#2F82FF', '#569BFF', '#7FB0FF'];

// Picks n evenly spaced colors from the ramp, keeping maximum step separation.
// Beyond the ramp length the smallest categories share the lightest step
// (callers sort by amount, so the overflow is always the tail).
export function pickRampColors(n: number): string[] {
  if (n <= 0) return [];
  if (n === 1) return [CATEGORY_RAMP[3]];

  if (n >= CATEGORY_RAMP.length) {
    return Array.from({ length: n }, (_, i) => CATEGORY_RAMP[Math.min(i, CATEGORY_RAMP.length - 1)]);
  }

  const step = (CATEGORY_RAMP.length - 1) / (n - 1);

  return Array.from({ length: n }, (_, i) => CATEGORY_RAMP[Math.round(i * step)]);
}

export const INCOME_CATEGORIES: CategoryDescriptor[] = [
  {
    reportType: 'employment-credit-transactions',
    classification: 'Payroll',
    side: 'credit',
    labelKey: 'employerIncome',
    defaultLabel: 'Employer Income',
    kind: 'income',
  },
  {
    reportType: 'government-credit-transactions',
    classification: 'Government Income',
    side: 'credit',
    labelKey: 'governmentIncome',
    defaultLabel: 'Government Income',
    kind: 'income',
  },
  {
    reportType: 'tax-credit-transactions',
    classification: 'Tax Credits',
    side: 'credit',
    labelKey: 'taxCredits',
    defaultLabel: 'Tax Credits',
    kind: 'income',
  },
  // Generic catch-all for credits that fit no specific category
  {
    reportType: 'other-credit-transactions',
    classification: 'Other Income',
    side: 'credit',
    labelKey: 'otherIncome',
    defaultLabel: 'Other Income',
    kind: 'income',
  },
];

export const EXPENSE_CATEGORIES: CategoryDescriptor[] = [
  {
    reportType: 'rent-debit-transactions',
    classification: 'Housing',
    side: 'debit',
    labelKey: 'rent',
    defaultLabel: 'Rent',
    kind: 'expense',
  },
  {
    reportType: 'loan-debit-transactions',
    classification: 'Loans',
    side: 'debit',
    labelKey: 'loans',
    defaultLabel: 'Loans',
    kind: 'expense',
  },
  {
    reportType: 'insurance-debit-transactions',
    classification: 'Health/Insurance',
    side: 'debit',
    labelKey: 'insurance',
    defaultLabel: 'Insurance',
    kind: 'expense',
  },
  {
    reportType: 'utility-debit-transactions',
    classification: 'Utilities',
    side: 'debit',
    labelKey: 'utilities',
    defaultLabel: 'Utilities',
    kind: 'expense',
  },
  {
    reportType: 'telecom-debit-transactions',
    classification: 'Telecom',
    side: 'debit',
    labelKey: 'telecom',
    defaultLabel: 'Telecom',
    kind: 'expense',
  },
  {
    reportType: 'subscription-debit-transactions',
    classification: 'Subscriptions/Memberships',
    side: 'debit',
    labelKey: 'subscriptions',
    defaultLabel: 'Subscriptions',
    kind: 'expense',
  },
  // Generic catch-all for debits that fit no specific category
  {
    reportType: 'other-debit-transactions',
    classification: 'Other Expense',
    side: 'debit',
    labelKey: 'otherExpense',
    defaultLabel: 'Other Expense',
    kind: 'expense',
  },
];

export const FEE_CATEGORIES: CategoryDescriptor[] = [
  {
    reportType: 'nsf-fee-transactions',
    // NSF charges come back as credits in the aggregator data
    classification: 'NSF',
    side: 'credit',
    labelKey: 'nsfFee',
    defaultLabel: 'NSF Fee',
    kind: 'fee',
  },
  {
    reportType: 'bank-fee-transactions',
    classification: 'Financial Services',
    side: 'debit',
    labelKey: 'bankFee',
    defaultLabel: 'Bank Fee',
    kind: 'fee',
  },
];

export const ALL_CATEGORIES: CategoryDescriptor[] = [...INCOME_CATEGORIES, ...EXPENSE_CATEGORIES, ...FEE_CATEGORIES];
